import React, { useMemo, useEffect, useRef, useState } from 'react';
import { useSound } from '../../utils/sound/SoundProvider';
import useGameBase from '../../hooks/useGameBase';

const MOVE_FLUSH_MS = 50;

function RedLightGreenLightGame({ gameState, gamePlayers, user, onGameAction, isSpectator, onPlayAgain, socket, roomId }) {
  const sound = useSound();
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = useMemo(() => normalizedState || gameState || {}, [normalizedState, gameState]);
  const meId = user ? (user.socketId || user.userId) : null;

  const positions = React.useMemo(() => (state.positions || {}), [state.positions]);
  const caught = React.useMemo(() => (state.caught || {}), [state.caught]);
  const light = state.light || 'red';
  const finished = !!state.finished;
  const moveDistance = Number(state.moveDistance || 1);
  const finishDistance = Number(state.finishDistance || 10);

  const [caughtNotice, setCaughtNotice] = useState(false);
  const [optimisticPosition, setOptimisticPosition] = useState(0);
  const prevCaughtAtRef = useRef(0);
  const prevLatestCaughtAtRef = useRef(0);
  const pendingMoveCountRef = useRef(0);
  const moveFlushTimerRef = useRef(null);

  const flushPendingMoves = React.useCallback(() => {
    if (!submitAction || pendingMoveCountRef.current <= 0 || finished || isSpectator) return;
    const count = pendingMoveCountRef.current;
    pendingMoveCountRef.current = 0;
    submitAction({ type: 'move', count }).catch(() => {
      pendingMoveCountRef.current += count;
    });
  }, [finished, isSpectator, submitAction]);

  const scheduleMoveFlush = React.useCallback(() => {
    if (moveFlushTimerRef.current) return;
    moveFlushTimerRef.current = setTimeout(() => {
      moveFlushTimerRef.current = null;
      flushPendingMoves();
    }, MOVE_FLUSH_MS);
  }, [flushPendingMoves]);

  useEffect(() => {
    prevCaughtAtRef.current = 0;
    prevLatestCaughtAtRef.current = 0;
    setCaughtNotice(false);
    pendingMoveCountRef.current = 0;
    setOptimisticPosition(typeof positions[meId] === 'number' ? positions[meId] : 0);
    if (moveFlushTimerRef.current) {
      clearTimeout(moveFlushTimerRef.current);
      moveFlushTimerRef.current = null;
    }
  }, [state.phase, state.started, state.finished, meId]);

  useEffect(() => {
    const serverPos = typeof positions[meId] === 'number' ? positions[meId] : 0;
    setOptimisticPosition((prev) => {
      if (serverPos >= prev) return serverPos;
      return prev;
    });
  }, [meId, positions]);

  useEffect(() => {
    const latestCaughtAt = Object.values(caught).reduce((latest, value) => {
      return typeof value === 'number' && value > latest ? value : latest;
    }, 0);

    if (latestCaughtAt > prevLatestCaughtAtRef.current) {
      try {
        if (sound && sound.enabled && sound.enabled()) {
          sound.play('ui:negative');
        }
      } catch (e) {}
      prevLatestCaughtAtRef.current = latestCaughtAt;
    }
  }, [caught, sound]);

  // Ensure caught notice always hides after 1s, even if caught events are rapid
  const caughtTimerRef = useRef();
  useEffect(() => {
    const myCaughtAt = typeof caught[meId] === 'number' ? caught[meId] : 0;

    if (myCaughtAt > prevCaughtAtRef.current) {
      setCaughtNotice(true);
      pendingMoveCountRef.current = 0;
      setOptimisticPosition(0);
      if (caughtTimerRef.current) clearTimeout(caughtTimerRef.current);
      caughtTimerRef.current = setTimeout(() => {
        setCaughtNotice(false);
        caughtTimerRef.current = null;
      }, 1000);
      prevCaughtAtRef.current = myCaughtAt;
    }

    prevCaughtAtRef.current = myCaughtAt;
    // Cleanup on unmount
    return () => {
      if (caughtTimerRef.current) {
        clearTimeout(caughtTimerRef.current);
        caughtTimerRef.current = null;
      }
    };
  }, [caught, meId]);

  useEffect(() => {
    if (light !== 'green' || finished) {
      if (moveFlushTimerRef.current) {
        clearTimeout(moveFlushTimerRef.current);
        moveFlushTimerRef.current = null;
      }
      flushPendingMoves();
    }
  }, [finished, flushPendingMoves, light]);

  useEffect(() => () => {
    if (moveFlushTimerRef.current) {
      clearTimeout(moveFlushTimerRef.current);
      moveFlushTimerRef.current = null;
    }
  }, []);

  const disabled = isSpectator || finished;

  const handleMove = () => {
    if (disabled) return;
    // Clear the caught notice as soon as the player attempts to move again
    setCaughtNotice(false);
    pendingMoveCountRef.current += 1;
    if (light === 'green') {
      setOptimisticPosition((prev) => Math.min(finishDistance, (prev || 0) + moveDistance));
    }
    scheduleMoveFlush();
  };

  const percentFor = (pos) => {
    const finish = finishDistance;
    const p = Math.min(100, Math.round((pos / finish) * 100));
    return p;
  };

  return (
    <div className="p-4 max-w-md mx-auto">
      <div className="mb-4">
        <div className={`w-full rounded-md py-2 font-semibold ${finished ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200' : ' text-gray-800 dark:text-gray-200'}`}>
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-1">
              {[0,1,2].map(i => (
                <span key={`l-${i}`} className={`w-4 h-4 rounded-full ${light === 'green' ? 'bg-green-400' : 'bg-red-500'}`}></span>
              ))}
            </div>

            <div className="mx-2 text-center">
              {finished ? 'Finished' : (light === 'green' ? 'Green — Go' : 'Red — Stop')}
            </div>

            <div className="flex items-center gap-1">
              {[0,1,2].map(i => (
                <span key={`r-${i}`} className={`w-4 h-4 rounded-full ${light === 'green' ? 'bg-green-400' : 'bg-red-500'}`}></span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {(state.players || []).map((p) => {
          const id = p.socketId || p.userId || p.id;
          const pos = id === meId ? Math.max(positions[id] || 0, optimisticPosition || 0) : (positions[id] || 0);
          const percent = percentFor(pos);
          const isMe = id === meId;
          const nameColorClass = percent >= 50 ? 'text-white' : (isMe ? 'text-blue-700' : 'text-gray-800 dark:text-gray-200');
          const wasCaught = state.caught && state.caught[id] && ((Date.now() - state.caught[id]) < 1800);
          return (
            <div key={id} className="flex items-center gap-3">
              <div className="flex-1">
                <div className={`w-full bg-gray-200 dark:bg-gray-700 rounded h-8 relative overflow-hidden ${wasCaught ? 'ring-2 ring-red-300' : ''}`}>
                  <div style={{ width: `${percent}%` }} className={`absolute left-0 top-0 h-full bg-indigo-400`}></div>
                  <div className="relative z-10 px-3 flex items-center justify-between h-full">
                    <div className={`font-medium truncate ${nameColorClass}`}>{p.username || id}</div>
                    <div className={`text-sm font-medium text-gray-600 dark:text-gray-400`}>{pos}/{state.finishDistance || 10}</div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-5 text-center">
        <button
          onClick={handleMove}
          disabled={disabled}
          className={`px-6 py-3 rounded-md text-white font-semibold ${light === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          {light === 'green' ? 'Go' : 'Stop'}
        </button>
        {caughtNotice && (
          <div className="mt-2 text-sm text-red-600">You were caught and sent back to start!</div>
        )}
      </div>
    </div>
  );
}

RedLightGreenLightGame.Guide = function RedLightGreenLightGuide() {
  return (
    <div>
      <h3 className="font-bold mb-2">Red Light Green Light</h3>
<p>The aim of this game is to reach the finish line by moving only when the light is green. If you move when the light is red, you'll be sent back to the start.</p>
      
    </div>
  );
};


export default RedLightGreenLightGame;
